'use client';

import { useEffect, useState } from 'react';
import type { TocHeading } from '@/lib/blog';

type Props = { headings: TocHeading[] };

export function TableOfContents({ headings }: Props) {
  const [active, setActive] = useState<string | null>(
    headings.length > 0 ? headings[0].id : null
  );

  useEffect(() => {
    if (headings.length === 0) return;
    const elements = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActive(visible[0].target.id);
        }
      },
      {
        rootMargin: '-96px 0px -60% 0px',
        threshold: [0, 1],
      }
    );

    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className="hidden xl:block sticky"
      style={{
        top: 'calc(var(--nav-height) + 24px)',
        background: 'rgba(20, 18, 16, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
        border: '1px solid rgba(255, 255, 255, 0.04)',
      }}
    >
      <h2
        className="font-mono text-text-muted mb-4"
        style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em' }}
      >
        ON THIS PAGE
      </h2>
      <ul className="flex flex-col gap-1.5">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              data-active={active === h.id}
              className="toc-link block font-mono transition-colors"
              style={{
                fontSize: 12,
                color: 'var(--text-muted)',
                paddingLeft: h.level === 3 ? 14 : 0,
                borderLeft: h.level === 3 ? '1px solid var(--border)' : 'none',
                marginLeft: h.level === 3 ? 4 : 0,
                lineHeight: 1.6,
              }}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
